import { stripAeoMarkdownBodies } from 'src/lib/aeo-markdown';
import { getBlogRegionQuery } from 'src/lib/blog-regions';
import { fetchTagsWithMinPosts } from 'src/lib/blog-tag-helpers';
import {
  BLOG_ENTRIES_PER_PAGE,
  CmsComponents,
  ContentfulTemplates,
  Locales,
  isTesting,
  type TLocale,
} from 'src/lib/constants';
import { blogCmsClient } from 'src/lib/contentful-client';
import { optimizeContentfulImages } from 'src/lib/image-helpers';
import {
  fetchAllEntries,
  generatePagePaths,
  isGeneratedPageParam,
} from 'src/lib/utils';

type Query = Record<string, unknown>;
type Fields = Record<string, unknown>;
type NotFound = { notFound: true };

const getEntries = (query: Query) => {
  return blogCmsClient.getEntries(query as never);
};

const getPosts = (locale: TLocale, query: Query = {}) => {
  return getEntries({
    content_type: ContentfulTemplates.BLOG_POST, // eslint-disable-line
    ...getBlogRegionQuery(locale),
    ...query,
  });
};

const getOldestOverview = async (locale: TLocale) => {
  const res = await getEntries({
    content_type: ContentfulTemplates.BLOG_OVERVIEW, // eslint-disable-line
    ...getBlogRegionQuery(locale),
    order: ['sys.createdAt'],
    limit: 1,
  });
  return res.items[0];
};

export const fetchBlogOverviewFields = async (
  locale: TLocale,
): Promise<Fields> => {
  const regionalOverview =
    locale === Locales.EN_US ? undefined : await getOldestOverview(locale);
  const overview = regionalOverview ?? (await getOldestOverview(Locales.EN_US));
  if (!overview) throw new Error('No published blog overview entry found');
  return overview.fields as Fields;
};

const fetchTagId = async (tag: string): Promise<string | undefined> => {
  const res = await getEntries({
    content_type: CmsComponents.TAG, // eslint-disable-line
    'fields.tag[in]': [tag],
  });
  return res.items[0]?.sys.id;
};

const countPages = (total: number, options?: Record<string, string>) => {
  return (
    generatePagePaths({ total, max: BLOG_ENTRIES_PER_PAGE, options }).length + 1
  );
};

export const getBlogIndexProps = async ({
  locale,
  preview,
}: {
  locale: TLocale;
  preview?: boolean;
}) => {
  const [fields, entries, allTags] = await Promise.all([
    fetchBlogOverviewFields(locale),
    getPosts(locale, {
      skip: 0,
      limit: BLOG_ENTRIES_PER_PAGE,
      order: ['-fields.published'],
    }),
    fetchTagsWithMinPosts(locale),
  ]);

  return {
    props: stripAeoMarkdownBodies({
      fields,
      posts: optimizeContentfulImages(entries.items),
      postsCount: entries.total,
      pageCount: Math.ceil(entries.total / BLOG_ENTRIES_PER_PAGE),
      allTags,
      preview: !!preview,
      locale,
    }),
  };
};

export const getBlogPagePaths = async ({ locale }: { locale: TLocale }) => {
  const entries = await getPosts(locale, { limit: 1 });
  const paths = generatePagePaths({
    total: entries.total,
    max: BLOG_ENTRIES_PER_PAGE,
  });
  return { paths: isTesting ? [] : paths, fallback: false };
};

export const getBlogPageProps = async ({
  locale,
  page,
}: {
  locale: TLocale;
  page: string;
}) => {
  if (!isGeneratedPageParam(page)) return { notFound: true } as NotFound;

  const [overviewFields, entries, allTags] = await Promise.all([
    fetchBlogOverviewFields(locale),
    getPosts(locale, {
      include: 10,
      skip: BLOG_ENTRIES_PER_PAGE * (Number(page) - 1),
      limit: BLOG_ENTRIES_PER_PAGE,
      order: ['-fields.published'],
    }),
    fetchTagsWithMinPosts(locale),
  ]);

  const totalPages = countPages(entries.total);
  if (Number(page) > totalPages) return { notFound: true } as NotFound;

  return {
    props: stripAeoMarkdownBodies({
      posts: entries.items,
      overviewFields,
      pageNum: Number(page),
      totalPages,
      allTags,
      locale,
    }),
  };
};

const fetchTagSlugsForLocale = async (locale: TLocale) => {
  const posts = await fetchAllEntries({
    contentType: ContentfulTemplates.BLOG_POST,
    query: {
      ...getBlogRegionQuery(locale),
      select: ['fields.tags'],
      include: 1,
    },
  });
  const slugs = new Set<string>();
  posts.forEach((post) => {
    const tags = (post.fields?.tags ?? []) as unknown as {
      fields?: { tag?: string };
    }[];
    tags.forEach((tag) => {
      if (tag?.fields?.tag) slugs.add(tag.fields.tag);
    });
  });
  return [...slugs];
};

const fetchRegionalOnlyTagSlugs = async () => {
  const [usSlugs, ukSlugs, euSlugs] = await Promise.all([
    fetchTagSlugsForLocale(Locales.EN_US),
    fetchTagSlugsForLocale(Locales.EN_GB),
    fetchTagSlugsForLocale(Locales.EN_EU),
  ]);
  const us = new Set(usSlugs);
  return new Set(
    [...ukSlugs, ...euSlugs].filter((slug) => {
      return !us.has(slug);
    }),
  );
};

export const getBlogTagPaths = async ({ locale }: { locale: TLocale }) => {
  let slugs: string[];
  if (locale === Locales.EN_US) {
    const [tags, regionalOnly] = await Promise.all([
      fetchAllEntries({ contentType: CmsComponents.TAG }),
      fetchRegionalOnlyTagSlugs(),
    ]);
    slugs = tags
      .map(({ fields }) => {
        return fields?.tag as unknown as string;
      })
      .filter((tag) => {
        return tag && !regionalOnly.has(tag);
      });
  } else {
    slugs = await fetchTagSlugsForLocale(locale);
  }

  const paths = slugs.map((tag) => {
    return { params: { tag } };
  });
  return { paths: isTesting ? [] : paths, fallback: false };
};

export const getBlogTagProps = async ({
  locale,
  tag,
  preview,
}: {
  locale: TLocale;
  tag: string;
  preview?: boolean;
}) => {
  const tagId = await fetchTagId(tag);
  if (!tagId) return { notFound: true } as NotFound;

  const [fields, entries, allTags] = await Promise.all([
    fetchBlogOverviewFields(locale),
    getPosts(locale, {
      links_to_entry: tagId, // eslint-disable-line
      include: 10,
      skip: 0,
      limit: BLOG_ENTRIES_PER_PAGE,
      order: ['-fields.published'],
    }),
    fetchTagsWithMinPosts(locale),
  ]);

  if (locale !== Locales.EN_US && entries.total === 0) {
    return { notFound: true } as NotFound;
  }

  return {
    props: stripAeoMarkdownBodies({
      fields,
      posts: optimizeContentfulImages(entries.items),
      postsCount: entries.total,
      pageCount: Math.ceil(entries.total / BLOG_ENTRIES_PER_PAGE),
      allTags,
      tag,
      preview: !!preview,
      locale,
    }),
  };
};

export const getBlogTagPagePaths = async ({ locale }: { locale: TLocale }) => {
  const [allTags, localeSlugs] = await Promise.all([
    fetchAllEntries({ contentType: CmsComponents.TAG }),
    locale === Locales.EN_US ? null : fetchTagSlugsForLocale(locale),
  ]);
  const regionalSlugs = localeSlugs ? new Set(localeSlugs) : null;
  const tags = allTags.filter(({ fields }) => {
    const tag = fields?.tag as unknown as string;
    return tag && (!regionalSlugs || regionalSlugs.has(tag));
  });
  const counts = await Promise.all(
    tags.map(({ sys }) => {
      return getPosts(locale, {
        links_to_entry: sys.id, // eslint-disable-line
        limit: 1,
      });
    }),
  );

  const paths = counts.flatMap(({ total }, index) => {
    const tag = tags[index].fields.tag as unknown as string;
    if (total <= BLOG_ENTRIES_PER_PAGE) return [];
    return generatePagePaths({
      total,
      max: BLOG_ENTRIES_PER_PAGE,
      options: { tag },
    });
  });

  return { paths: isTesting ? [] : paths, fallback: false };
};

export const getBlogTagPageProps = async ({
  locale,
  tag,
  page,
}: {
  locale: TLocale;
  tag: string;
  page: string;
}) => {
  if (!isGeneratedPageParam(page)) return { notFound: true } as NotFound;

  const tagId = await fetchTagId(tag);
  if (!tagId) return { notFound: true } as NotFound;

  const [entries, overviewFields, allTags] = await Promise.all([
    getPosts(locale, {
      'fields.tags.sys.id': tagId,
      limit: BLOG_ENTRIES_PER_PAGE,
      skip: BLOG_ENTRIES_PER_PAGE * (Number(page) - 1),
      order: ['-fields.published'],
    }),
    fetchBlogOverviewFields(locale),
    fetchTagsWithMinPosts(locale),
  ]);

  const totalPages = countPages(entries.total, { tag });
  if (Number(page) > totalPages) return { notFound: true } as NotFound;

  return {
    props: stripAeoMarkdownBodies({
      posts: entries.items,
      overviewFields,
      pageNum: Number(page),
      totalPages,
      tag,
      allTags,
      locale,
    }),
  };
};

export const getBlogPostPaths = async ({ locale }: { locale: TLocale }) => {
  const entries = await fetchAllEntries({
    contentType: ContentfulTemplates.BLOG_POST,
    query: getBlogRegionQuery(locale),
  });

  const paths = entries
    .map((entry) => {
      return entry.fields?.pageSlug as unknown as string;
    })
    .filter(Boolean)
    .map((post) => {
      return { params: { post } };
    });

  return { paths: isTesting ? [] : paths, fallback: false };
};

export const getBlogPostProps = async ({
  locale,
  post,
  preview,
}: {
  locale: TLocale;
  post: string;
  preview?: boolean;
}) => {
  const entries = await getPosts(locale, {
    'fields.pageSlug': post,
    include: 10,
  });
  if (!entries.items[0]) return { notFound: true } as NotFound;

  const data = { ...entries.items[0], preview: !!preview, locale };
  return { props: optimizeContentfulImages(data) };
};
